import React from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { CmiDigestWatchlistCommunityRow } from 'types/cmiDigestWatchlist';

import { SCROLL_FADE_HEIGHT } from './constants';
import { Column, ColumnTitle, ColumnTitleCount, EmptyMessage } from './DigestSectionLayout';

const List = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-bottom: ${SCROLL_FADE_HEIGHT}px;
  border-top: solid 1px var(--line-gray);
  mask-image: linear-gradient(to bottom, black calc(100% - ${SCROLL_FADE_HEIGHT}px), transparent);
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
  gap: 15px;
  padding: 15px 0;
  border-bottom: solid 1px var(--line-gray);
`;

const Accent = styled.div`
  width: 4px;
  flex-shrink: 0;
  border-radius: 4px;
  background: linear-gradient(to bottom, var(--amber-light), var(--inactive-orange));
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowHeader = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
`;

const CommunityName = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const DetailsLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 12px;
  color: var(--dark-blue);
  text-decoration: none;
  cursor: pointer;
`;

const NotableItems = styled.div`
  margin-top: 5px;
  font-size: 14px;
  color: var(--amber-dark);
`;

const Summary = styled.div`
  display: -webkit-box;
  margin-top: 5px;
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

type Props = {
  rows: CmiDigestWatchlistCommunityRow[];
  runId: number;
};

const NeedsAttentionColumn = ({ rows, runId }: Props) => (
  <Column data-test-id="communities-to-watch-needs-attention">
    <ColumnTitle>
      <span>
        Needs Attention <ColumnTitleCount>({rows.length})</ColumnTitleCount>
      </span>
    </ColumnTitle>
    {rows.length === 0 ? (
      <EmptyMessage>No communities needed your attention in this digest.</EmptyMessage>
    ) : (
      <List className="mini-scroll">
        {rows.map((row) => (
          <Row key={row.id}>
            <Accent />
            <Content>
              <RowHeader>
                <CommunityName>{row.community_name}</CommunityName>
                <DetailsLink to={`/communities-to-watch/${runId}/communities/${row.id}`}>
                  Details <ChevronRight fill="var(--dark-blue)" width={16} height={16} />
                </DetailsLink>
              </RowHeader>
              {row.notable_items.length > 0 && <NotableItems>{row.notable_items.join(' · ')}</NotableItems>}
              {/* .replace() below will get rid of the markup bold text. It's a design decision not to show bold text
              of an insight in this place */}
              {row.summary.length > 0 && <Summary>{row.summary.join(' ').replace(/\*\*/g, '')}</Summary>}
            </Content>
          </Row>
        ))}
      </List>
    )}
  </Column>
);

export default NeedsAttentionColumn;
